import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { CalibrationPoint } from "../../../../types";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (point: CalibrationPoint) => void;
  point?: CalibrationPoint;
  x: number;
  y: number;
  rampId: string;
};

export const CalibrationPointDialog: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  point,
  x,
  y,
  rampId,
}) => {
  const { t } = useTranslation();
  const [coordinates, setCoordinates] = React.useState("");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");

  React.useEffect(() => {
    if (open) {
      // Clear form when dialog opens to prevent showing previous point's data
      if (point) {
        setLatitude(point.latitude.toString());
        setLongitude(point.longitude.toString());
        setCoordinates(`${point.latitude}, ${point.longitude}`);
      } else {
        setLatitude("");
        setLongitude("");
        setCoordinates("");
      }
    }
  }, [point, open]);

  const handleCoordinatesChange = (value: string) => {
    setCoordinates(value);

    // Try to parse coordinates from the string
    const parts = value.split(",").map((s) => s.trim());
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        setLatitude(lat.toString());
        setLongitude(lng.toString());
      }
    }
  };

  const handleSave = () => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      return;
    }

    const calibrationPoint: CalibrationPoint = {
      id: point?.id || crypto.randomUUID(),
      ramp_id: rampId,
      x,
      y,
      latitude: lat,
      longitude: lng,
    };

    onSave(calibrationPoint);
    onClose();
  };

  const isValid = !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {point
          ? t("settings.rampsSettings.rampEditor.calibrationPointDialog.titleEdit")
          : t("settings.rampsSettings.rampEditor.calibrationPointDialog.titleAdd")}
      </DialogTitle>
      <DialogContent sx={{ py: 1 }}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("settings.rampsSettings.rampEditor.calibrationPointDialog.position", {
              x: (x * 100).toFixed(1),
              y: (y * 100).toFixed(1)
            })}
          </Typography>

          <TextField
            fullWidth
            label={t("settings.rampsSettings.rampEditor.calibrationPointDialog.coordinatesLabel")}
            placeholder={t("settings.rampsSettings.rampEditor.calibrationPointDialog.coordinatesPlaceholder")}
            value={coordinates}
            onChange={(e) => handleCoordinatesChange(e.target.value)}
            size="small"
          />

          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              label={t("settings.rampsSettings.rampEditor.calibrationPointDialog.latitudeLabel")}
              type="number"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              inputProps={{ step: "any" }}
              size="small"
            />
            <TextField
              fullWidth
              label={t("settings.rampsSettings.rampEditor.calibrationPointDialog.longitudeLabel")}
              type="number"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              inputProps={{ step: "any" }}
              size="small"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          {t("settings.rampsSettings.rampEditor.calibrationPointDialog.cancelButton")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
          size="small"
        >
          {point
            ? t("settings.rampsSettings.rampEditor.calibrationPointDialog.updateButton")
            : t("settings.rampsSettings.rampEditor.calibrationPointDialog.addButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
